/* ==========================================================================
   Decant motion layer
   --------------------------------------------------------------------------
   Every animation here has a job:
     hero lines      hierarchy    the promise lands one line at a time
     hero parallax   depth        the set sits behind the type, not on it
     rail reveal     storytelling the occasions arrive as a row, not at once
     index sticky    orientation  the aside says which family you are reading
     process pin     sequence     three steps in the order they happen
     close parallax  arrival      the page settles onto the last call
   Nothing loops, nothing hijacks the scroll, nothing moves without a reason.

   Start states are set by GSAP at creation time, never by CSS, so a failed
   script leaves a complete readable page rather than blank boxes.
   ========================================================================== */

(function () {
  'use strict';

  var gsap = window.gsap;
  var ScrollTrigger = window.ScrollTrigger;
  var SplitText = window.SplitText;

  if (!gsap || !ScrollTrigger) return;
  gsap.registerPlugin(ScrollTrigger);
  if (SplitText) gsap.registerPlugin(SplitText);

  gsap.defaults({ ease: 'power3.out', duration: 0.9 });

  var contexts = new WeakMap();

  var EASE_OUT = 'power3.out';
  var BONE = '#edeae3';
  var FAINT = '#8a8983';

  /* ------------------------------------------------------------ utilities */

  function all(root, selector) {
    return Array.prototype.slice.call(root.querySelectorAll(selector));
  }

  /** Smooth 0 to 1 across [a, b]. Used to stagger copy inside a panel. */
  function ramp(v, a, b) {
    var t = Math.min(1, Math.max(0, (v - a) / (b - a)));
    return t * t * (3 - 2 * t);
  }

  /** Hex or rgb() to [r, g, b]. Theme colour settings hand back hex. */
  function rgb(value) {
    var s = String(value || '').trim();
    var m = s.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
    if (m) {
      var h = m[1];
      if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
      return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
    }
    var nums = s.match(/[\d.]+/g);
    if (nums && nums.length >= 3) return nums.slice(0, 3).map(Number);
    return [11, 12, 14];
  }

  function mixRgb(a, b, t) {
    t = Math.min(1, Math.max(0, t));
    return [
      Math.round(a[0] + (b[0] - a[0]) * t),
      Math.round(a[1] + (b[1] - a[1]) * t),
      Math.round(a[2] + (b[2] - a[2]) * t)
    ];
  }

  /** The header sits transparent over the hero and takes a ground past it.
      Deliberately outside the reduced-motion gate: this is legibility, not
      decoration, and the nav must stay readable over the bright panels. */
  function headerState() {
    var group = document.getElementById('header-group');
    var hero = document.querySelector('[data-dc-section="hero"]');
    if (!group || !hero || group.dataset.dcBound) return;
    group.dataset.dcBound = '1';

    ScrollTrigger.create({
      trigger: hero,
      start: 'bottom 88px',
      end: 'max',
      onToggle: function (self) {
        group.classList.toggle('dc-head-solid', self.isActive);
      }
    });
  }

  /** Masked line reveal. Falls back to a plain rise when SplitText is absent. */
  function revealHeading(el, opts) {
    opts = opts || {};
    var trigger = opts.scroll
      ? { trigger: el, start: 'top 88%', once: true }
      : undefined;

    if (!SplitText) {
      return gsap.fromTo(
        el,
        { y: 26, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          delay: opts.delay || 0,
          ease: EASE_OUT,
          clearProps: 'transform',
          scrollTrigger: trigger
        }
      );
    }

    return SplitText.create(el, {
      type: 'lines',
      mask: 'lines',
      autoSplit: true,
      linesClass: 'dc-splitline',
      onSplit: function (self) {
        return gsap.fromTo(
          self.lines,
          { yPercent: 118 },
          {
            yPercent: 0,
            duration: 1.05,
            delay: opts.delay || 0,
            ease: EASE_OUT,
            stagger: 0.085,
            scrollTrigger: trigger
          }
        );
      }
    });
  }

  /** A photograph that drifts inside the box that clips it, so it reads as
      sitting a little behind the page rather than pasted onto it.

      Two things about this were wrong and both were visible on a phone.

      The travel was one-directional: the picture started centred and slid to
      -8% of its own height. It is scaled up to give it room, and a scale of
      1.12 leaves exactly 6% of overhang on each side, so the last 2% of that
      slide pulled the bottom edge of the photograph into frame and a strip of
      the figure's own dark ground appeared under it. Now the travel is split
      either side of centre, so it uses the headroom evenly, and the scale is
      derived from the distance instead of being passed in independently. The
      two can no longer disagree.

      The scrub was a number. A numeric scrub is not smoothing, it is a
      catch-up lag: the picture chases the scroll position and takes that many
      seconds to arrive. Scrolling one way and flicking back the other leaves
      it still travelling the old way for well over half a second before it
      turns around, which is the picture visibly coming loose from the page
      and then snapping back into it. scrub: true ties it to the scroll
      position frame for frame, so it moves only when the page moves and stops
      the instant the page stops. */
  function parallaxImage(img, trigger, distance, scale) {
    if (!img) return;

    var travel = Math.abs(distance || 8);

    /* Overhang each side is (scale - 1) / 2 of the height, and the picture
       moves travel/2 either way, so scale must be at least 1 + travel/100.
       The extra 2% absorbs subpixel rounding at fractional device pixel
       ratios, where a hairline of the ground could otherwise show. */
    var needed = 1 + travel / 100 + 0.02;
    var used = Math.max(scale || 1, needed);

    gsap.set(img, { scale: used, transformOrigin: 'center center' });

    gsap.fromTo(
      img,
      { yPercent: travel / 2 },
      {
        yPercent: -travel / 2,
        ease: 'none',
        scrollTrigger: {
          trigger: trigger,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
          invalidateOnRefresh: true
        }
      }
    );
  }

  /** Collapse repeated calls into one per animation frame.

      scrub: true updates the moment a scroll position changes, which can be
      several times inside a single frame. That is free for a transform, but
      the portal rewrites the font size and the baseline of every line of SVG
      text, and the strip writes a custom property per panel, and doing either
      of those two or three times before the browser paints once is pure waste.
      Measured on a 1680 desktop it took long frames from 1.6% to 12.4%.

      The work still happens on the frame the scroll happened, so the picture
      is still locked to the page; it just happens once. */
  function perFrame(fn) {
    var queued = false;
    var args = null;
    return function () {
      args = arguments;
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        fn.apply(null, args);
      });
    };
  }

  /* fromTo, never from, for anything a ScrollTrigger drives.

     A from tween has no written end state: it records whatever the element
     measures at the moment it is built and animates back to that. Two things
     in this file call ScrollTrigger.refresh() on a timer nobody controls,
     document.fonts.ready and window load, and a refresh re-reads the values
     of a from tween that rendered immediately. Both land around a second
     after the page opens, which is the middle of a staggered entrance, so the
     items that had not arrived yet had their half way position recorded as
     the place they were heading. Measured on the hero: the first button ended
     at 0 and the two after it parked at y 20.5 and 22.0 of a 22 pixel rise,
     and stayed there. This is also what was washing those elements out to
     about a tenth of their opacity.

     Writing both ends means a refresh has nothing to guess. */
  /** Generic entrance for anything marked up with data-dc-reveal. */
  function revealGroup(items, opts) {
    if (!items.length) return;
    opts = opts || {};
    gsap.fromTo(
      items,
      { y: opts.y === undefined ? 30 : opts.y, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.95,
        ease: EASE_OUT,
        stagger: opts.stagger === undefined ? 0.08 : opts.stagger,
        clearProps: 'transform',
        scrollTrigger: {
          trigger: opts.trigger || items[0].parentNode,
          start: opts.start || 'top 84%',
          once: true
        }
      }
    );
  }

  /* ------------------------------------------------------------- sections */


  /* Slow colour drift. Each blob gets its own loop so they never line up into
     an obvious rhythm, plus a scroll offset so the field moves with the page
     rather than sitting on top of it. */
  function driftHaze(haze, trigger) {
    if (!haze) return;
    var blobs = all(haze, '.dc-haze__blob');
    if (!blobs.length) return;

    blobs.forEach(function (blob, i) {
      gsap.to(blob, {
        xPercent: i % 2 ? -14 : 12,
        yPercent: i === 1 ? 10 : -8,
        duration: 16 + i * 5,
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true
      });

      gsap.to(blob, {
        yPercent: '+=' + (i === 1 ? -26 : 20),
        ease: 'none',
        scrollTrigger: {
          trigger: trigger,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 0.8
        }
      });
    });
  }

  var builders = {
    hero: function (root, wide) {
      var title = root.querySelector('[data-dc-split]');
      if (title) revealHeading(title, { delay: 0.1 });

      revealGroup(all(root, '[data-dc-hero-in]'), {
        y: 22,
        stagger: 0.09,
        trigger: root,
        start: 'top 95%'
      });

      driftHaze(root.querySelector('[data-dc-haze]'), root);

      var bg = root.querySelector('.dc-hero__bg img');
      if (bg) {
        /* The photograph settles on load rather than sitting still: it starts
           slightly too close and pulls back as the headline lands, so the two
           read as one movement instead of text appearing over a static plate. */
        gsap.fromTo(
          bg,
          { scale: wide ? 1.08 : 1.16 },
          { scale: wide ? 1.08 : 1.02, duration: 1.6, ease: EASE_OUT }
        );

        gsap.to(bg, {
          yPercent: wide ? 12 : 16,
          ease: 'none',
          scrollTrigger: { trigger: root, start: 'top top', end: 'bottom top', scrub: true }
        });
      }

      if (!wide) {
        /* On a phone the copy is the whole hero, so give it depth: the words
           leave faster than the picture behind them. */
        var copy = root.querySelector('.dc-hero__copy');
        if (copy) {
          gsap.to(copy, {
            yPercent: -18,
            opacity: 0.35,
            ease: 'none',
            scrollTrigger: { trigger: root, start: 'top top', end: 'bottom 40%', scrub: 0.4 }
          });
        }
        return;
      }

      var stills = all(root, '.dc-hero__still');
      stills.forEach(function (fig, i) {
        parallaxImage(fig.querySelector('img'), root, i === 0 ? -7 : -13, 1.14);
      });

      if (stills.length) {
        gsap.fromTo(
          stills,
          { y: 48, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 1.2,
            delay: 0.25,
            ease: EASE_OUT,
            stagger: 0.12,
            clearProps: 'transform'
          }
        );
      }
    },

    /* ------------------------------------------------------------- portal
       Scroll through the headline. The words carry the photograph as their
       fill, so scaling the line scales the picture inside the letterforms;
       past a point the letters are bigger than the screen and the full frame
       image behind them takes over. */
    /* ------------------------------------------------------------- portal
       A sheet of ink over a photograph with the words cut out of it. Scroll
       and the words grow, so the holes grow, until the sheet is gone and the
       photograph is all that is left. The picture never moves and never fades
       in: it is behind the paper the whole time.

       SVG rather than background-clip on an HTML heading, for two reasons.
       Scaling an HTML element rasterises it once and stretches the result,
       which is what made the letter edges go soft; SVG text driven by its own
       font-size is re-rendered every frame and stays sharp. And a mask is a
       real cutout, where clipping a background to text only ever paints the
       picture inside the glyphs. */
    portal: function (root, wide) {
      var stage = root.querySelector('.dc-portal__stage');
      var svg = root.querySelector('.dc-portal__cut');
      var word = root.querySelector('[data-dc-portal-word]');
      var bg = root.querySelector('.dc-portal__bg img');
      if (!stage || !svg || !word) return;

      var textEl = svg.querySelector('[data-dc-type-el]');
      var sheets = all(svg, '[data-dc-sheet]');
      var heading = (word.textContent || '').trim();
      if (!textEl || !heading) return;

      driftHaze(root.querySelector('[data-dc-haze]'), root);

      var lines = [];
      var spans = [];
      var base = 48;
      var peak = 14;

      /* Measured with the real element rather than a canvas, so the variable
         font's width axis and the letter spacing are accounted for. */
      function widthAt(str, size) {
        textEl.setAttribute('font-size', size);
        textEl.textContent = str;
        var w = textEl.getComputedTextLength();
        textEl.textContent = '';
        return w;
      }

      function wrapAt(size, maxWidth) {
        var words = heading.split(/\s+/);
        var out = [];
        var cur = '';
        for (var i = 0; i < words.length; i++) {
          var test = cur ? cur + ' ' + words[i] : words[i];
          if (cur && widthAt(test, size) > maxWidth) {
            out.push(cur);
            cur = words[i];
          } else {
            cur = test;
          }
        }
        if (cur) out.push(cur);
        return out;
      }

      function build() {
        var w = stage.clientWidth;
        var h = stage.clientHeight;
        if (!w || !h) return;

        sheets.forEach(function (rect) {
          rect.setAttribute('width', w);
          rect.setAttribute('height', h);
        });

        var maxWidth = w * 0.88;
        var trial = Math.max(24, Math.min(w, h) * 0.14);

        lines = wrapAt(trial, maxWidth);
        var widest = 0;
        for (var i = 0; i < lines.length; i++) {
          widest = Math.max(widest, widthAt(lines[i], trial));
        }
        base = widest ? trial * (maxWidth / widest) : trial;

        /* Re-wrap at the fitted size, because a bigger size can push a word
           down and change the shape of the block. */
        lines = wrapAt(base, maxWidth);
        widest = 0;
        for (i = 0; i < lines.length; i++) {
          widest = Math.max(widest, widthAt(lines[i], base));
        }
        if (widest > maxWidth) base *= maxWidth / widest;

        /* And keep the block inside the screen vertically. */
        var blockH = lines.length * base * 1.02;
        if (blockH > h * 0.78) base *= (h * 0.78) / blockH;

        /* Grow until one letter is comfortably bigger than the screen. */
        peak = Math.max(8, Math.min(26, (h * 1.9) / (base * 1.02)));

        while (textEl.firstChild) textEl.removeChild(textEl.firstChild);
        spans = lines.map(function (line) {
          var t = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
          t.textContent = line;
          textEl.appendChild(t);
          return t;
        });

        place(base);
        root.classList.add('is-cut');
      }

      var lastX = null;
      var dead = false;

      function place(size) {
        /* A coalesced place is queued for the next frame, so one can still be
           in flight after the section has been torn down and the tspans
           detached. */
        if (dead) return;
        var w = stage.clientWidth;
        var h = stage.clientHeight;
        var lh = size * 1.02;
        /* 0.74em puts the cap height either side of the centre line, which is
           what reads as vertically centred for this face. */
        var first = h / 2 - (spans.length * lh) / 2 + size * 0.74;
        var x = w / 2;
        /* x only moves when the stage is resized, so it does not belong in a
           loop that runs on every frame of the zoom. */
        var setX = x !== lastX;
        lastX = x;
        textEl.setAttribute('font-size', size);
        for (var i = 0; i < spans.length; i++) {
          if (setX) spans[i].setAttribute('x', x);
          spans[i].setAttribute('y', first + i * lh);
        }
      }

      var placeSoon = perFrame(place);

      build();
      if (!spans.length) return;

      /* Still by default.

         Everything above this line is what makes the picture show through the
         letters: the measuring, the wrap, the sheet, the cutout. None of that
         is the animation. The animation is only the timeline below, so a
         still portal is this function stopping here with the headline sized
         to the screen, rather than a separate code path that could drift away
         from the moving one.

         It has to measure again afterwards, twice.

         Once when the box changes: the fitted size comes from the stage, and
         on a phone that changes when the address bar moves.

         And once when the webfont arrives. build() sizes the line by asking
         the browser how wide it actually is, so if it runs while the fallback
         face is still in place it measures the wrong letters and fits the
         wrong size. Caught here the first time this ran still: at 390 it
         settled on 55px for one line, and when the real face swapped in that
         line was wider than the screen and lost a letter off each end. The
         moving version never showed this because every ScrollTrigger refresh
         rebuilt it, and fonts.ready fires one. Nothing refreshes a still
         section, so it has to ask for itself. */
      if (root.hasAttribute('data-dc-still')) {
        var rebuild = perFrame(function () {
          lastX = null;
          build();
        });
        window.addEventListener('resize', rebuild);
        window.addEventListener('orientationchange', rebuild);
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(rebuild);
        }
        return function () {
          dead = true;
          window.removeEventListener('resize', rebuild);
          window.removeEventListener('orientationchange', rebuild);
          root.classList.remove('is-cut');
          while (textEl.firstChild) textEl.removeChild(textEl.firstChild);
        };
      }

      /* Zoom is multiplicative, so the ramp has to be too.

         This ran the font size up a straight line, base to base times peak.
         That is not a straight zoom: going 55px to 1430px linearly spends the
         first half of the scroll covering a thirteen fold expansion and the
         whole second half covering less than two, so it launches and then
         crawls. Raising peak to the power of progress keeps the rate of
         growth constant, which is what actually reads as moving through the
         word at a steady speed. */
      var state = { t: 0 };

      function sizeAt(t) {
        return base * Math.pow(peak, t);
      }

      var tl = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: stage,
          start: 'top top',
          end: function () { return '+=' + Math.round(window.innerHeight * (wide ? 1.7 : 1.5)); },
          pin: true,
          anticipatePin: 1,

          /* scrub: true, not a number, and no fastScrollEnd.

             A numeric scrub is a catch-up lag, so the letters kept growing
             after the scroll stopped and took nearly half a second to turn
             round when it reversed. fastScrollEnd is worse: it exists to
             throw the timeline straight to its end state when you scroll
             quickly, which on a phone flick is a single frame where the word
             leaps most of the way through the zoom. Measured at 30fps, the
             photograph behind the letters went from 36% to 54% of the screen
             between two consecutive frames. Both are gone; the zoom is now
             tied to the scroll position and moves only as far as the page
             does. */
          scrub: true,
          invalidateOnRefresh: true,
          onRefresh: function (self) {
            build();
            state.t = self.progress;
            lastX = null;
            place(sizeAt(state.t));
          }
        }
      });

      tl.fromTo(
        state,
        { t: 0 },
        {
          t: 1,
          duration: 1,
          onUpdate: function () { placeSoon(sizeAt(state.t)); }
        },
        0
      );

      /* The last of the paper. If the screen happens to land on a stem rather
         than inside a counter, this is what guarantees a clean finish. The
         photograph never fades: only the sheet over it does. */
      tl.to(svg, { opacity: 0, duration: 0.12 }, 0.88);

      if (bg) tl.fromTo(bg, { scale: 1.16 }, { scale: 1, duration: 1 }, 0);

      return function () {
        dead = true;
        root.classList.remove('is-cut');
        gsap.set(svg, { clearProps: 'opacity' });
        while (textEl.firstChild) textEl.removeChild(textEl.firstChild);
      };
    },

    pan: function (root) {
      var stage = root.querySelector('.dc-strip__stage');
      var track = root.querySelector('.dc-strip__track');
      var viewport = root.querySelector('.dc-strip__viewport');
      var panels = all(root, '.dc-panel');
      if (!stage || !track || !viewport || panels.length < 2) return;

      var n = panels.length;
      var wash = root.querySelector('.dc-strip__wash');
      var idxEl = root.querySelector('[data-dc-index]');
      var nowEl = root.querySelector('[data-dc-now]');
      var metaEl = root.querySelector('[data-dc-meta]');
      var railEl = root.querySelector('[data-dc-rail]');

      var tints = panels.map(function (p) { return rgb(p.getAttribute('data-tint')); });
      var names = panels.map(function (p) { return p.getAttribute('data-name') || ''; });
      var metas = panels.map(function (p) { return p.getAttribute('data-meta') || ''; });

      root.classList.add('is-pan');

      /* Measured, not assumed: the card width is a CSS clamp, so read what the
         browser actually laid out and re-read it on every refresh. */
      var step = 0;
      var startX = 0;

      function measure() {
        var a = panels[0].getBoundingClientRect();
        var b = panels[1].getBoundingClientRect();
        step = b.left - a.left;
        startX = viewport.getBoundingClientRect().width / 2 - a.width / 2;
      }

      var state = { p: 0 };
      var shown = -1;

      /* Writing a custom property invalidates style for that element even when
         the value has not changed. With four panels and five properties each
         that was twenty invalidations per frame for values that are often
         identical two frames running, which is what made the scrub feel
         heavy. Only write what actually moved. */
      var lastVars = [];

      function setVar(el, i, key, value) {
        var slot = lastVars[i] || (lastVars[i] = {});
        if (slot[key] === value) return;
        slot[key] = value;
        el.style.setProperty(key, value);
      }

      var dead = false;

      function paint() {
        /* A coalesced paint is queued for the next frame, so one can still be
           in flight when the section is torn down in the theme editor. Without
           this it would write the custom properties back after cleanup had
           removed them. */
        if (dead) return;
        var a = state.p * (n - 1);

        gsap.set(track, { x: startX - a * step });

        for (var j = 0; j < n; j++) {
          var d = Math.abs(j - a);
          var near = Math.max(0, 1 - d);
          near = near * near * (3 - 2 * near);
          var el = panels[j];
          setVar(el, j, '--near', near.toFixed(2));
          setVar(el, j, '--shift', ((j - a) * -5).toFixed(2));
          /* Three thresholds, so name, line and button arrive one after the
             other rather than all together. */
          setVar(el, j, '--r1', ramp(near, 0.20, 0.55).toFixed(2));
          setVar(el, j, '--r2', ramp(near, 0.38, 0.72).toFixed(2));
          setVar(el, j, '--r3', ramp(near, 0.55, 0.90).toFixed(2));
        }

        var lo = Math.max(0, Math.min(n - 1, Math.floor(a)));
        var hi = Math.min(n - 1, lo + 1);
        var c = mixRgb(tints[lo], tints[hi], a - lo);

        root.style.setProperty('--dc-tint', 'rgba(' + c[0] + ',' + c[1] + ',' + c[2] + ',0.45)');
        if (wash) {
          wash.style.backgroundColor = 'rgb(' + c[0] + ',' + c[1] + ',' + c[2] + ')';
          wash.style.opacity = '0.9';
        }
        if (railEl) railEl.style.setProperty('--p', state.p.toFixed(4));

        var i = Math.round(a);
        if (i !== shown) {
          shown = i;
          if (idxEl) idxEl.textContent = ('0' + (i + 1)).slice(-2);
          if (nowEl) nowEl.textContent = names[i];
          if (metaEl) metaEl.textContent = metas[i];
        }
      }

      /* Same reason as the portal: with scrub: true the tween can be advanced
         more than once between two paints, and the row only needs drawing for
         the position it will actually be shown at. */
      var paintSoon = perFrame(paint);

      measure();
      paint();

      gsap.to(state, {
        p: 1,
        ease: 'none',
        onUpdate: paintSoon,
        scrollTrigger: {
          trigger: stage,
          start: 'top top',
          end: function () {
            return '+=' + Math.round(window.innerHeight * (n - 1) * 0.85);
          },
          pin: true,
          /* Locked to the scroll for the same reason as the portal: a numeric
             scrub here left the cards still travelling after the flick had
             stopped, which is the row sliding on its own. */
          scrub: true,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          onRefresh: function () { measure(); paint(); }
        }
      });

      return function () {
        dead = true;
        root.classList.remove('is-pan');
        gsap.set(track, { clearProps: 'transform' });
        panels.forEach(function (el) {
          ['--near', '--shift', '--r1', '--r2', '--r3'].forEach(function (v) {
            el.style.removeProperty(v);
          });
        });
      };
    },

    index: function (root) {
      var head = root.querySelector('[data-dc-split]');
      if (head) revealHeading(head, { scroll: true });

      var families = all(root, '.dc-family');

      families.forEach(function (family) {
        var id = family.getAttribute('id');
        var link = id ? root.querySelector('.dc-index__nav a[href="#' + id + '"]') : null;
        var item = link ? link.parentNode : null;

        gsap.fromTo(
          family.querySelectorAll('[data-dc-fade]'),
          { y: 26, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.9,
            ease: EASE_OUT,
            stagger: 0.06,
            clearProps: 'transform',
            scrollTrigger: { trigger: family, start: 'top 82%', once: true }
          }
        );

        parallaxImage(family.querySelector('.dc-family__media img'), family, -8, 1.12);

        if (!item) return;
        ScrollTrigger.create({
          trigger: family,
          start: 'top 45%',
          end: 'bottom 45%',
          onToggle: function (self) {
            item.classList.toggle('is-current', self.isActive);
          }
        });
      });
    },

    process: function (root) {
      var head = root.querySelector('[data-dc-split]');
      if (head) revealHeading(head, { scroll: true });

      var bg = root.querySelector('.dc-process__bg img');
      if (bg) {
        gsap.set(bg, { scale: 1.1 });
        gsap.to(bg, {
          yPercent: 8,
          ease: 'none',
          scrollTrigger: { trigger: root, start: 'top bottom', end: 'bottom top', scrub: true }
        });
      }

      /* The cards deal themselves in. The flip itself is wired outside this
         builder, because a visitor who asks for reduced motion still needs
         the cards to open. */
      revealGroup(all(root, '.dc-flip'), {
        y: 26,
        stagger: 0.09,
        trigger: root,
        start: 'top 74%'
      });
    },

    proof: function (root) {
      var quote = root.querySelector('[data-dc-split]');
      if (quote) revealHeading(quote, { scroll: true });

      revealGroup(all(root, '[data-dc-fade]'), {
        y: 24,
        stagger: 0.07,
        trigger: root,
        start: 'top 76%'
      });
    },

    faq: function (root) {
      var head = root.querySelector('[data-dc-split]');
      if (head) revealHeading(head, { scroll: true });

      revealGroup(all(root, '.dc-qa'), {
        y: 22,
        stagger: 0.055,
        trigger: root,
        start: 'top 80%'
      });
    },

    close: function (root) {
      var head = root.querySelector('[data-dc-split]');
      if (head) revealHeading(head, { scroll: true });

      revealGroup(all(root, '[data-dc-fade]'), {
        y: 20,
        stagger: 0.09,
        trigger: root,
        start: 'top 72%'
      });

      var bg = root.querySelector('.dc-close__bg img');
      if (bg) {
        gsap.set(bg, { scale: 1.16 });
        gsap.to(bg, {
          yPercent: -10,
          scale: 1.03,
          ease: 'none',
          scrollTrigger: { trigger: root, start: 'top bottom', end: 'bottom top', scrub: 0.7 }
        });
      }
    }
  };


  /* --------------------------------------------------------------- scroller
     Horizon does not scroll the window on desktop. base.css puts
     height:100dvh and overflow:hidden on html and body at 990px and up, and
     scrolls a .page-wrapper div instead. ScrollTrigger defaults to the
     window, so above that width every trigger was attached to a scroller that
     never moves: the pins still reserved their space, which is why the page
     had long empty stretches, but nothing ever fired.

     Below 990px the window scrolls normally, and the theme editor's preview
     pane is narrower than the breakpoint, which is why this only showed up in
     a full width preview tab. */

  var scrollHost = null;

  /* A phone's address bar collapsing changes innerHeight, which ScrollTrigger
     would otherwise treat as a resize and re-measure the whole page mid
     scroll. That is felt as a jolt. */
  ScrollTrigger.config({ ignoreMobileResize: true });

  function resolveScroller() {
    var wrap = document.querySelector('.page-wrapper');
    if (!wrap) return window;
    var oy = getComputedStyle(wrap).overflowY;
    return oy === 'auto' || oy === 'scroll' ? wrap : window;
  }

  function applyScroller() {
    var host = resolveScroller();
    if (host === scrollHost) return false;
    scrollHost = host;
    /* pinType matters here. With a scroller that is not the window GSAP
       defaults to "transform", which re-translates the pinned element every
       frame and lands a frame behind the scroll, so the pinned section
       visibly trembles. .page-wrapper fills the viewport, so a pinned element
       positioned fixed to the viewport is in exactly the right place and the
       browser holds it there for free. */
    ScrollTrigger.defaults({ scroller: host, pinType: 'fixed' });
    return true;
  }

  /* ------------------------------------------------------------- flip cards
     Deliberately outside the matchMedia gate above. That gate exists so that
     someone who asks for reduced motion gets the page with no animation, but
     a card that will not open is a broken control, not a calm one. So the
     flip is always wired; only the typing is conditional. */

  var TYPE_CAP = 2.6;

  function typeOut(card, el, text) {
    var state = { n: 0 };
    card.classList.add('is-typing');
    return gsap.to(state, {
      n: text.length,
      duration: Math.min(TYPE_CAP, text.length * 0.02),
      ease: 'none',
      onUpdate: function () {
        el.textContent = text.slice(0, Math.round(state.n));
      },
      onComplete: function () {
        el.textContent = text;
        card.classList.remove('is-typing');
      }
    });
  }

  function wireFlip(card) {
    if (card.dataset.dcFlipWired) return;
    card.dataset.dcFlipWired = '1';

    var copy = card.querySelector('[data-dc-type]');
    /* Read the copy off the markup once, before anything clears it, so the
       no-script and reduced-motion paths keep a finished paragraph. */
    var text = copy ? copy.textContent.trim() : '';
    var faces = all(card, '.dc-flip__face');
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var tween = null;

    function setHidden(open) {
      faces.forEach(function (face) {
        var isBack = face.classList.contains('dc-flip__face--back');
        face.setAttribute('aria-hidden', isBack === open ? 'false' : 'true');
      });
    }

    setHidden(false);

    card.addEventListener('click', function () {
      var open = card.getAttribute('aria-expanded') !== 'true';
      card.setAttribute('aria-expanded', open ? 'true' : 'false');
      setHidden(open);

      if (tween) tween.kill();
      if (!copy || !text) return;

      if (!open) {
        card.classList.remove('is-typing');
        copy.textContent = text;
        return;
      }

      if (reduce) {
        copy.textContent = text;
        return;
      }

      copy.textContent = '';
      /* Start typing as the card lands, not while it is still edge on. */
      tween = gsap.delayedCall(0.34, function () {
        tween = typeOut(card, copy, text);
      });
    });
  }

  /* ---------------------------------------------------------- orchestration */

  function mount(section) {
    if (!section || contexts.has(section)) return;

    var kind = section.getAttribute('data-dc-section');
    var build = builders[kind];
    if (!build) return;

    var mm = gsap.matchMedia();

    mm.add(
      {
        motion: '(prefers-reduced-motion: no-preference)',
        wide: '(min-width: 860px)'
      },
      function (ctx) {
        /* Reduced motion gets the finished page with no animation at all:
           because every start state is a GSAP from(), skipping the build
           leaves the markup exactly as the browser rendered it. */
        if (!ctx.conditions.motion) return;
        /* A builder may return a cleanup for anything GSAP cannot revert on
           its own, such as a class it added. matchMedia runs it on revert. */
        return build(section, ctx.conditions.wide);
      },
      section
    );

    contexts.set(section, mm);
  }

  function unmount(section) {
    var mm = contexts.get(section);
    if (!mm) return;
    mm.revert();
    contexts.delete(section);
  }

  function mountAll(scope) {
    all(scope || document, '[data-dc-section]').forEach(mount);
    all(scope || document, '[data-dc-flip]').forEach(wireFlip);
    headerState();
  }

  /* ScrollTrigger measures in creation order, so mount sections top to bottom. */
  function boot() {
    applyScroller();
    mountAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Crossing 990px swaps which element scrolls, and a ScrollTrigger cannot be
     moved to a different scroller after it is built. Tear the whole layer down
     and put it back on the new one. */
  var resizeTimer = 0;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      if (resolveScroller() === scrollHost) return;
      all(document, '[data-dc-section]').forEach(unmount);
      var group = document.getElementById('header-group');
      if (group) delete group.dataset.dcBound;
      ScrollTrigger.getAll().forEach(function (t) { t.kill(); });
      applyScroller();
      mountAll();
      ScrollTrigger.refresh();
    }, 220);
  });

  /* Web fonts change line boxes, which changes every start and end value. */
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () { ScrollTrigger.refresh(); });
  }
  window.addEventListener('load', function () { ScrollTrigger.refresh(); });

  /* Back button restores from the bfcache with stale measurements. */
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) ScrollTrigger.refresh();
  });

  /* Theme editor: sections are swapped in and out of the DOM live. */
  document.addEventListener('shopify:section:load', function (event) {
    mountAll(event.target);
    ScrollTrigger.refresh();
  });

  document.addEventListener('shopify:section:unload', function (event) {
    all(event.target, '[data-dc-section]').forEach(unmount);
    if (event.target.hasAttribute && event.target.hasAttribute('data-dc-section')) {
      unmount(event.target);
    }
    ScrollTrigger.refresh();
  });
})();